import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { DocEntityType } from "../doc/docEntityTypes";
import type { AllForeignKeyFieldKeys, DocEntityForeignKeyConstraints } from "./docEntityConstraintTypes";

/**
 * `undefined` is only a valid entry for an entity type with zero FK fields. This keeps the
 * exhaustiveness check meaningful: an entity type that later gains an FK field can no longer be
 * left as `undefined` and must get a real constraint entry (see `AllForeignKeyFieldKeys`).
 */
type DocEntityConstraints = {
  readonly [T in DocEntityType]: [AllForeignKeyFieldKeys<DocEntityTypeToValueType[T]>] extends [never]
    ? DocEntityForeignKeyConstraints<DocEntityTypeToValueType[T]> | undefined
    : DocEntityForeignKeyConstraints<DocEntityTypeToValueType[T]>;
};
/**
 * Registry of doc-entity FK constraint definitions.
 *
 * This object is currently the primary source of truth for documenting and driving
 * doc-ref relationship behavior:
 * - which references are required vs ignored while importing/updating doc refs
 * - where traversal should stop at a boundary entity
 * - where reverse doc-ref traversal is explicitly enabled
 *
 * Formula-wrapped ForeignKeyString fields (`disabled?: boolean | Formula`, `number | Formula`,
 * etc.) are valid constraint keys. Their `formula` strings reference OPTION_SET/OPTION/ELEVATION
 * or parameters; those conditions must not cascade-delete the referrer (`onDelete: "IGNORE"`).
 * Parameter formulas can also mention option sets (ActiveOptionState / drawing-instance Conditions
 * Builder). Viewport/data-table Building State Overrides (`optionSetOverrides`) use the same IGNORE
 * delete rule so a removed OPTION_SET does not delete the viewport.
 *
 * These constraints are consumed by generators and runtime doc-entity traversal tools
 * to derive doc-ref eligibility and dependency expansion behavior.
 */
const formulaOptionSetConstraints = {
  OPTION_SET: { onDocRef: "IGNORE", onDelete: "IGNORE" },
  OPTION: { onDocRef: "IGNORE", onDelete: "IGNORE" },
  ELEVATION: { onDocRef: "IGNORE", onDelete: "IGNORE" },
} as const;

const formulaParameterConstraints = {
  GLOBAL_PARAMETER: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
  SYSTEM_PARAMETER: { onDocRef: "IGNORE", onDelete: "IGNORE" },
  LOCAL_PARAMETER: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
  ...formulaOptionSetConstraints,
} as const;

const optionSetOverrideConstraints = {
  OPTION_SET: { onDocRef: "IGNORE", onDelete: "IGNORE" },
  OPTION: { onDocRef: "IGNORE", onDelete: "IGNORE" },
} as const;

const labelConstraints = {
  LABEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
} as const;

const profileRequiredConstraints = {
  PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "REQUIRED" },
  PROFILE: { onDocRef: "REQUIRED" },
} as const;

const profileIgnoreConstraints = {
  PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
  PROFILE: { onDocRef: "IGNORE" },
} as const;

const parameterDefaultValueConstraints = {
  COMPOSITE_LEVEL: { onDocRef: "REQUIRED" },
  CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
  DATA_SEQUENCE: { onDocRef: "REQUIRED" },
  ELEVATION_VIEW: { onDocRef: "IGNORE" },
  LABEL: { onDocRef: "REQUIRED" },
  LEVEL: { onDocRef: "REQUIRED" },
  OPTION: { onDocRef: "REQUIRED" },
  PRESET: { onDocRef: "REQUIRED" },
  SECTION_VIEW: { onDocRef: "IGNORE" },
  VIEW_TEMPLATE: { onDocRef: "REQUIRED" },
} as const;

export const docEntityConstraints: DocEntityConstraints = {
  ANNOTATION: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
    text: {
      GLOBAL_PARAMETER: { onDocRef: "REQUIRED" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "REQUIRED" },
    },
    assetId: {
      ASSET: { onDocRef: "REQUIRED" },
    },
  },
  ASSET: undefined,
  AUTO_DIMENSION: undefined,
  BLOCK: {
    disabled: formulaOptionSetConstraints,
    levelId: {
      LEVEL: { onDocRef: "REQUIRED" },
    },
  },
  CASEWORK: {
    disabled: formulaOptionSetConstraints,
    depth: formulaOptionSetConstraints,
    height: formulaOptionSetConstraints,
    lateralOffset: formulaOptionSetConstraints,
    verticalOffset: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    catalogDescriptorId: {
      CASEWORK_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  CATALOG_ITEM: {
    catalogId: {
      DRAWING_CATALOG_SETTINGS: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE" },
    },
    propAssetId: {
      ASSET: { onDocRef: "IGNORE" },
    },
    propId: {
      PROP: { onDocRef: "IGNORE" },
    },
  },
  CEILING: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    labels: labelConstraints,
  },
  COLUMN: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    catalogColumnId: {
      COLUMN_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    isolatedAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
  },
  COMPONENT: undefined,
  COMPONENT_INSTANCE: {
    disabled: formulaOptionSetConstraints,
    componentId: {
      COMPONENT: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
  },
  COMPOSITE_LEVEL: {
    members: {
      LEVEL: { onDocRef: "IGNORE" },
      COMPONENT_INSTANCE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  CONTENT_REPLICATOR: {
    disabled: formulaParameterConstraints,
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
    },
    sequenceId: {
      DATA_SEQUENCE: { onDocRef: "REQUIRED" },
    },
  },
  CONTENT_TEMPLATE: { labels: labelConstraints },
  CONTENT_TEMPLATE_INSTANCE: {
    disabled: formulaParameterConstraints,
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE" },
      PLAN_SET_TEMPLATE: { onDocRef: "REQUIRED" },
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
    },
    labels: labelConstraints,
    parameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    contentTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
    },
  },
  DATA_SEQUENCE: undefined,
  DATA_TABLE: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    optionSetOverrides: optionSetOverrideConstraints,
    dataSequenceId: {
      DATA_SEQUENCE: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    levelId: {
      LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    presetId: {
      PRESET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
  },
  DATUM: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
  },
  DOC_REF: undefined,
  DORMER: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOF: { onDocRef: "IGNORE" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  DRAWING_REF: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      SHEET: { onDocRef: "REQUIRED" },
    },
    drawingId: {
      DRAWING: { onDocRef: "REQUIRED" },
    },
  },
  EDGE: undefined,
  EDGE_BEHAVIOR: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    facadeId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
    foundationAssemblyOverrides: {
      DATUM: { onDocRef: "IGNORE" },
      DRAWING: { onDocRef: "IGNORE" },
      FOUNDATION_ASSEMBLY: { onDocRef: "IGNORE" },
      ...formulaOptionSetConstraints,
    },
  },
  EDGE_MODIFIER: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      RAILING: { onDocRef: "IGNORE" },
      ROOF: { onDocRef: "IGNORE" },
      FLOOR: { onDocRef: "IGNORE" },
      FOUNDATION: { onDocRef: "IGNORE" },
    },
    modifierId: {
      MODIFIER: { onDocRef: "REQUIRED" },
    },
  },
  EDIT_MODE: undefined,
  EFFECT: {
    parentId: {
      VIEW_TEMPLATE_OVERRIDE: { onDocRef: "REQUIRED" },
    },
  },
  ELECTRICAL_DEVICE: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    loadType: {
      LOAD_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    selections: {
      ROOM: { onDocRef: "IGNORE" },
    },
    labels: labelConstraints,
  },
  ELECTRICAL_LAYOUT: { disabled: formulaOptionSetConstraints },
  ELECTRICAL_RULE: {
    parentId: {
      SETTINGS: { onDocRef: "REQUIRED" },
    },
  },
  ELEVATION: undefined,
  ELEVATION_VIEW: undefined,
  ENTITY_SETTINGS: {
    entityId: {
      DORMER: { onDocRef: "IGNORE", onDocRefReverseReferences: "REQUIRED" },
      FLOOR: { onDocRef: "STOP_AT", onDocRefReverseReferences: "REQUIRED" },
      FOUNDATION: { onDocRef: "STOP_AT", onDocRefReverseReferences: "REQUIRED" },
      OPENING: { onDocRef: "STOP_AT", onDocRefReverseReferences: "REQUIRED" },
      ROOF: { onDocRef: "IGNORE", onDocRefReverseReferences: "REQUIRED" },
      ROOF_OPENING: { onDocRef: "IGNORE", onDocRefReverseReferences: "REQUIRED" },
      ROOM: { onDocRef: "STOP_AT", onDocRefReverseReferences: "REQUIRED" },
    },
    settingsOrSettingsSetId: {
      SETTINGS_SET: { onDocRef: "REQUIRED" },
      SETTINGS: { onDocRef: "REQUIRED" },
    },
  },
  FACADE: { disabled: formulaOptionSetConstraints },
  FACADE_SPAN_STRATUM: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
  },
  FACADE_SWEEP_STRATUM: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      FACADE: { onDocRef: "REQUIRED" },
    },
  },
  FLOOR: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      FLOOR_SYSTEM: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
    },
    labels: labelConstraints,
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    foundationAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    topDatum: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    assemblyId: {
      FRAME_FLOOR_ASSEMBLY: {
        onDocRef: "REQUIRED",
      },
      SLAB_FLOOR_ASSEMBLY: {
        onDocRef: "REQUIRED",
      },
    },
    floorJoist: {
      FRAMING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    rimBoard: {
      FRAMING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    slab: {
      SLAB_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
    subFloor: {
      SHEATHING_COMPONENT: {
        onDocRef: "REQUIRED",
      },
    },
  },
  FLOOR_FINISH: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "REQUIRED" },
    },
  },
  FLOOR_BAY: undefined,
  FLOOR_PERIMETER_OPENING: undefined,
  FLOOR_SYSTEM: {
    disabled: formulaOptionSetConstraints,
    assemblyId: {
      FRAME_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
      SLAB_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
    topDatum: {
      DATUM: { onDocRef: "REQUIRED" },
    },
  },
  FLOOR_SPLIT_LINE: undefined,
  FOUNDATION: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      FOUNDATION_SYSTEM: { onDocRef: "IGNORE" },
    },
    foundationEdgeOverrides: {
      DATUM: { onDocRef: "IGNORE" },
      FOUNDATION_ASSEMBLY: { onDocRef: "IGNORE" },
    },

    labels: labelConstraints,
    bottomDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    brickledgeTopDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    lateralDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    topDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    foundationAssemblyId: {
      FOUNDATION_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
  },
  FOUNDATION_ASSEMBLY: {
    bottomDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    brickledgeTopDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    lateralDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
    curbId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    footingId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    pierId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    stemWallId: {
      FOUNDATION_COMPONENT: { onDocRef: "REQUIRED" },
    },
    topDatumId: {
      DATUM: { onDocRef: "REQUIRED" },
    },
  },
  FOUNDATION_COMPONENT: undefined,
  FOUNDATION_SYSTEM: { disabled: formulaOptionSetConstraints },
  FRAME_FLOOR_ASSEMBLY: {
    joistComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
    rimComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
    subFloorComponentId: {
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  FRAMING_COMPONENT: undefined,
  FREEFORM_WALL: {
    disabled: formulaOptionSetConstraints,
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    labels: labelConstraints,
  },
  GENERATED_ENTITY: undefined,
  GLOBAL_PARAMETER: {
    defaultValue: parameterDefaultValueConstraints,
    optionSetId: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
  },
  GROUP: { disabled: formulaOptionSetConstraints },
  HEADER_ASSEMBLY: {
    headerComponentId: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  INDETERMINATE_ENTITY_TYPE: undefined,
  INSTANCE_OPTION_SET: {
    archetypeId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    componentInstanceId: {
      COMPONENT_INSTANCE: { onDocRef: "REQUIRED" },
    },
  },
  LABEL: undefined,
  LEVEL: { disabled: formulaOptionSetConstraints },
  LOCAL_PARAMETER: {
    defaultValue: parameterDefaultValueConstraints,
    optionSetId: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE" },
      PLAN_SET_TEMPLATE: { onDocRef: "IGNORE" },
    },
  },
  MEASUREMENT: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
  },
  MODIFIER: { disabled: formulaOptionSetConstraints },
  OPENING: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    doorType: {
      DOOR_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  OPTION: {
    parentId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
  },
  OPTION_EFFECT: {
    optionId: {
      OPTION: { onDocRef: "REQUIRED" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    parentId: {
      OPTION_RULE: { onDocRef: "REQUIRED" },
    },
  },
  OPTION_LOCK: {
    disconnectCondition: formulaOptionSetConstraints,
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    disconnectConditionOptions: formulaOptionSetConstraints,
  },
  OPTION_RULE: {
    predicate: formulaOptionSetConstraints,
  },
  OPTION_SET: {
    conditions: {
      OPTION: { onDocRef: "IGNORE" },
    },
    formatting: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    labels: {
      LABEL: { onDocRef: "IGNORE" },
    },
    options: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    scope: {
      OPTION_SET: { onDocRef: "IGNORE" },
      OPTION: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  OPTION_SET_OVERRIDES: {
    disconnectCondition: formulaOptionSetConstraints,
    options: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "REQUIRED" },
    },
    disconnectConditionOptions: formulaOptionSetConstraints,
    parentId: {
      OPTION_TEMPLATE: { onDocRef: "IGNORE" },
    },
  },
  OPTION_TEMPLATE: undefined,
  PALETTE: undefined,
  PLAN_SET_TEMPLATE: undefined,
  PLAN_SET_TEMPLATE_CONFIG: {
    parentId: {
      PLAN_SET_TEMPLATE: {
        onDocRef: "REQUIRED",
      },
    },
    parameterValues: {
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    globalParameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
    },
  },
  PLAN_VIEW: {
    hostLevelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    levelIds: {
      COMPOSITE_LEVEL: { onDocRef: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE" },
      COMPONENT_INSTANCE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    underlayLevelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
  },
  PRESET: {
    optionChoices: {
      OPTION_SET: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      OPTION: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  PROFILE: undefined,
  RAILING_SETTING: undefined,
  PROP: {
    propAssetId: {
      ASSET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    labels: labelConstraints,
  },
  PROP_INSTANCE: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    propType: {
      PROP: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
  },
  RAILING: {
    disabled: formulaOptionSetConstraints,
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    railingSettingId: {
      RAILING_SETTING: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    labels: labelConstraints,
  },
  RAILING_POST: {
    parentId: {
      RAILING: { onDocRef: "IGNORE" },
    },
  },
  REFERENCE_GEOM: undefined,
  REPORTABLE_UNIT_DESIGNATION: {
    labelId: {
      LABEL: { onDocRef: "REQUIRED", onDocRefReverseReferences: "REQUIRED" },
    },
  },
  ROOF: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOF_SYSTEM: { onDocRef: "REQUIRED" },
    },
    wallRoofBaseBoardProfile: profileRequiredConstraints,
    blocks: {
      BLOCK: { onDocRef: "IGNORE" },
      COMPONENT_INSTANCE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    labels: labelConstraints,
    gableEndLabels: {
      FACADE: { onDocRef: "IGNORE" },
    },
    roofSideOverrides: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
      PROFILE: { onDocRef: "IGNORE" },
      PROP: { onDocRef: "IGNORE" },
      ...formulaOptionSetConstraints,
    },
    roofEdgeOverrides: {
      PROFILE_EXTRUSION_DESCRIPTOR: { onDocRef: "IGNORE" },
      PROFILE: { onDocRef: "IGNORE" },
      PROP: { onDocRef: "IGNORE" },
      ...formulaOptionSetConstraints,
    },
  },
  ROOF_OPENING: {
    disabled: formulaOptionSetConstraints,
    offset: formulaOptionSetConstraints,
    parentId: {
      ROOF: { onDocRef: "REQUIRED" },
    },
    windowType: {
      WINDOW_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
  },
  ROOF_SIDE: {
    heelHeightOffset: formulaOptionSetConstraints,
    slope: formulaOptionSetConstraints,
    triangleGableVentBaseWidth: formulaOptionSetConstraints,
    truncationSlope: formulaOptionSetConstraints,
    eaveBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    gableBellyBandBoard: profileRequiredConstraints,
    includeFriezeBoardLegacy: profileRequiredConstraints,
    rakeBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    // Unlike its sibling profile fields above, friezeBoard is intentionally IGNORE-only (not required).
    friezeBoard: profileIgnoreConstraints,
  },
  ROOF_SYSTEM: { disabled: formulaOptionSetConstraints },
  ROOF_VOLUME: { disabled: formulaOptionSetConstraints },
  ROOM: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      BLOCK: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
    roomTagTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  ROOM_LAYOUT: {
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
  },
  ROOM_LAYOUT_INSTANCE: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOM: { onDocRef: "STOP_AT" },
    },
    roomLayoutId: {
      ROOM_LAYOUT: { onDocRef: "REQUIRED" },
    },
    suppressedEntities: {
      ANNOTATION: { onDocRef: "IGNORE" },
      CASEWORK: { onDocRef: "IGNORE" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "IGNORE" },
      OPENING: { onDocRef: "IGNORE" },
      PROP_INSTANCE: { onDocRef: "IGNORE" },
    },
  },
  RULE_BASED_SELECTOR: {
    rules: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
      LABEL: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
      OPTION: { onDocRef: "IGNORE" },
      OPTION_SET: { onDocRef: "IGNORE" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
    },
  },
  RULE_BASED_SELECTOR_INSTANCE: {
    parentId: {
      VIEW_TEMPLATE_OVERRIDE: { onDocRef: "REQUIRED" },
    },
    selectorId: {
      RULE_BASED_SELECTOR: { onDocRef: "REQUIRED" },
    },
  },
  SECTION_VIEW: {
    calloutLabels: {
      LABEL: { onDocRef: "IGNORE" },
    },
  },
  SELECTOR: {
    parentId: {
      COMPOSITE_LEVEL: { onDocRef: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
    },
    overrideId: {
      VIEW_OVERRIDE: { onDocRef: "IGNORE" },
    },
  },
  SETTINGS: {
    eaveOverhang: formulaOptionSetConstraints,
    rakeOverhang: formulaOptionSetConstraints,
    rightSlope: formulaOptionSetConstraints,
    slope: formulaOptionSetConstraints,
    doorTypes: {
      PROP: { onDocRef: "IGNORE" },
    },
    propTypes: {
      ASSET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    wallConditions: {
      WALL_TYPE: { onDocRef: "IGNORE" },
    },
    rules: {
      HEADER_ASSEMBLY: { onDocRef: "IGNORE" },
      SETTINGS: { onDocRef: "IGNORE" },
    },
    headerAssemblyId: {
      HEADER_ASSEMBLY: { onDocRef: "IGNORE" },
    },
    drawings: {
      DRAWING_CATALOG_SETTINGS: { onDocRef: "IGNORE" },
    },
    parentId: {
      SETTINGS_SET: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "REQUIRED" },
      ROOM: { onDocRef: "IGNORE" },
    },
    rakeFriezeBoard: profileRequiredConstraints,
    eaveFriezeBoard: profileRequiredConstraints,
    wallBaseBoard: profileRequiredConstraints,
    gableBellyBandBoard: profileRequiredConstraints,
    awningBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    baseBoardType: profileRequiredConstraints,
    columnId: {
      COLUMN_DESCRIPTOR: { onDocRef: "REQUIRED" },
    },
    cornerBoardProfile: profileRequiredConstraints,
    rakeBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    crownMoldingType: profileRequiredConstraints,
    dormerCornerBoardProfile: profileRequiredConstraints,
    eaveBracketPropId: {
      PROP: { onDocRef: "REQUIRED" },
    },
    floorAssembly: {
      FRAME_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
      SLAB_FLOOR_ASSEMBLY: { onDocRef: "REQUIRED" },
    },
    headTrimType: profileRequiredConstraints,
    insideCornerBoardProfile: profileRequiredConstraints,
    outsideCornerBoardProfile: profileRequiredConstraints,
    wallRoofBaseBoardProfile: profileRequiredConstraints,
    porchHeaderTrimProfile: profileRequiredConstraints,
    sideTrimType: profileRequiredConstraints,
  },
  SETTINGS_SET: { disabled: formulaOptionSetConstraints },
  SHAPE: undefined,
  CIP_COMPONENT: undefined,
  MASONRY_COMPONENT: undefined,
  SHEATHING_COMPONENT: undefined,
  SHEET: {
    disabled: formulaOptionSetConstraints,
    labels: labelConstraints,
  },
  SITE: { disabled: formulaOptionSetConstraints },
  SITE_SHAPE: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      SITE: { onDocRef: "REQUIRED" },
    },
  },
  SKYLIGHT: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      ROOF: { onDocRef: "REQUIRED" },
    },
  },
  SLAB_COMPONENT: undefined,
  SLAB_FLOOR_ASSEMBLY: {
    slabComponentId: {
      SLAB_COMPONENT: { onDocRef: "REQUIRED" },
    },
    subFloorComponentId: {
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
    },
  },
  STAIR_BLOCK: {
    disabled: formulaOptionSetConstraints,
    parentId: {
      STAIR_SYSTEM: { onDocRef: "IGNORE" },
    },
    stairSystemId: {
      STAIR_SYSTEM: { onDocRef: "IGNORE" },
    },
    datumVertical: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumVerticalLower: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
    datumVerticalUpper: {
      DATUM: { onDocRef: "REQUIRED" },
      LEVEL: { onDocRef: "IGNORE" },
      ROOM: { onDocRef: "STOP_AT" },
      STAIR_BLOCK: { onDocRef: "IGNORE" },
    },
  },
  STAIR_SETTING: undefined,
  STAIR_SYSTEM: {
    disabled: formulaOptionSetConstraints,
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    stairSettingId: {
      STAIR_CATALOG_SETTINGS: { onDocRef: "REQUIRED" },
      STAIR_SETTING: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
  },
  SYSTEM_PARAMETER: {
    defaultValue: {
      OPTION: { onDocRef: "IGNORE" },
      PRESET: { onDocRef: "IGNORE" },
    },
    optionSetId: {
      OPTION_SET: { onDocRef: "IGNORE" },
    },
  },
  UNDERLAY: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    viewId: {
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
    },
    assetId: {
      ASSET: { onDocRef: "REQUIRED" },
    },
  },
  VERTEX: undefined,
  VIEW_3D: {
    levelId: {
      LEVEL: { onDocRef: "IGNORE" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
  },
  VIEW_OVERRIDE: { disabled: formulaOptionSetConstraints },
  VIEW_SELECTOR: {
    overrideId: {
      VIEW_OVERRIDE: { onDocRef: "IGNORE" },
    },
    viewId: {
      ELEVATION_VIEW: { onDocRef: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE" },
      PLAN_VIEW: { onDocRef: "IGNORE" },
      VIEW_3D: { onDocRef: "IGNORE" },
    },
  },
  VIEW_TEMPLATE: {
    roomTagTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  VIEW_TEMPLATE_OVERRIDE: {
    labels: {
      LABEL: { onDocRef: "IGNORE" },
    },
  },
  VIEWPORT: {
    disabled: formulaParameterConstraints,
    parentId: {
      CONTENT_TEMPLATE: { onDocRef: "REQUIRED" },
      CONTENT_TEMPLATE_INSTANCE: { onDocRef: "REQUIRED" },
    },
    selectedReportingUnitLabelId: {
      LABEL: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    optionSetOverrides: optionSetOverrideConstraints,
    parameterValues: {
      GLOBAL_PARAMETER: { onDocRef: "IGNORE" },
      SYSTEM_PARAMETER: { onDocRef: "IGNORE" },
      LOCAL_PARAMETER: { onDocRef: "IGNORE" },
    },
    contentSourceId: {
      COMPOSITE_LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      LEVEL: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      SECTION_VIEW: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      ELEVATION_VIEW: { onDocRef: "IGNORE", onDelete: "IGNORE" },
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
    presetId: {
      PRESET: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    viewTemplateId: {
      VIEW_TEMPLATE: { onDocRef: "REQUIRED", onDelete: "IGNORE" },
    },
    titleDrawingTemplateId: {
      CONTENT_TEMPLATE: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  WALL_PLANE_ASSEMBLY: {
    layers: {
      FRAMING_COMPONENT: { onDocRef: "REQUIRED" },
      SHEATHING_COMPONENT: { onDocRef: "REQUIRED" },
      MASONRY_COMPONENT: { onDocRef: "REQUIRED" },
      CIP_COMPONENT: { onDocRef: "REQUIRED" },
    },
    labels: labelConstraints,
  },
  WALL_PLANE_FILTER: undefined,
  WALL_PLANE_RULE: {
    disabled: formulaOptionSetConstraints,
    filterId: {
      WALL_PLANE_FILTER: { onDocRef: "IGNORE" },
    },
    roofId: {
      ROOF: { onDocRef: "IGNORE" },
    },
    operationBoundary: {
      ROOF: { onDocRef: "IGNORE", onDelete: "IGNORE" },
    },
  },
  WALL_PLANE_TRIM: {
    disabled: formulaOptionSetConstraints,
    ruleId: {
      WALL_PLANE_RULE: { onDocRef: "IGNORE" },
    },
    profileId: profileIgnoreConstraints,
  },
};
